import { useCallback } from 'react';
// auth
import { useAuthContext } from 'src/auth/hooks'
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import {
  resetItems,
  selectInvoice,
  selectCustomer,
  updateItems,
  increaseQuantity,
  decreaseQuantity,
  updatePrice,
  nextStep,
  backStep,
  firstStep,
  updatePayment,
  updatePrintInvoice,
  updatePaymentMethod,
  selectVoucher,
  clearVoucher,
  updateVoucherAmount,
  applyDiscount,
  clearDiscount,
  applyItemDiscount,
  clearItemDiscount,
} from 'src/redux/slice/checkout';

// ----------------------------------------------------------------------

export default function useCheckout() {
  const { user } = useAuthContext()

  const config = user?.configSelected

  const taxIncludedInItemPrice = config?.taxIncludedInItemPrice

  const dispatch = useDispatch();

  const items = useSelector((state) => state.checkout.items);

  const invoiceSelected = useSelector((state) => state.checkout.invoiceSelected);

  const customerSelected = useSelector((state) => state.checkout.customerSelected);

  const activeStep = useSelector((state) => state.checkout.activeStep);

  const subTotal = useSelector((state) => state.checkout.subTotal);

  const total = useSelector((state) => state.checkout.total ? state.checkout.total : '0.00');

  const tax = useSelector((state) => (state.checkout.tax ? state.checkout.tax : '0.00'));

  const discount = useSelector((state) => {
    try {
      return state.checkout.discount ? state.checkout.discount.toFixed(2) : '0.00';
    } catch {
      return state.checkout.discount;
    }
  });

  const totalItems = useSelector((state) => state.checkout.totalItems);

  const payment = useSelector((state) => state.checkout.payment);

  const printInvoice = useSelector((state) => state.checkout.printInvoice);

  const paymentMethodSelected = useSelector((state) => state.checkout.paymentMethodSelected);

  const voucher = useSelector((state) => state.checkout.voucher);

  const voucherAmount = useSelector((state) => state.checkout.voucherAmount);

  const discountLabel = useSelector((state) => state.checkout.discountLabel || '');

  const onResetItems = useCallback(
    () => {
      dispatch(resetItems());
    },
    [dispatch],
  );

  const onSelectInvoice = useCallback(
    (data) => {
      dispatch(selectInvoice(data));
    },
    [dispatch],
  );

  const onSelectCustomer = useCallback(
    (data) => {
      dispatch(selectCustomer(data));
    },
    [dispatch],
  );

  const onUpdateItems = useCallback(
    (data) => {
      dispatch(updateItems({ data, taxConfig: config?.tax, taxIncludedInItemPrice }));
    },
    [dispatch, config?.tax, taxIncludedInItemPrice]
  );

  const onIncreaseQuantity = useCallback(
    (item) => {
      dispatch(increaseQuantity({ item, taxConfig: config?.tax || 0, taxIncludedInItemPrice }));
    },
    [config?.tax, taxIncludedInItemPrice, dispatch]
  );

  const onDecreaseQuantity = useCallback(
    (item) => {
      dispatch(decreaseQuantity({ item, taxConfig: config?.tax || 0, taxIncludedInItemPrice }));
    },
    [config?.tax, taxIncludedInItemPrice, dispatch]
  );

  const onUpdatePrice = useCallback(
    (item, price) => {
      dispatch(updatePrice({ item, price, taxConfig: config?.tax || 0, taxIncludedInItemPrice }));
    },
    [config?.tax, taxIncludedInItemPrice, dispatch]
  );

  const onNextStep = useCallback(
    () => {
      dispatch(nextStep());
    },
    [dispatch],
  );

  const onBackStep = useCallback(
    () => {
      dispatch(backStep());
    },
    [dispatch],
  );

  const onFirstStep = useCallback(
    () => {
      dispatch(firstStep());
    },
    [dispatch],
  );

  const onUpdatePayment = useCallback(
    (pay) => {
      dispatch(updatePayment(pay));
    },
    [dispatch],
  );

  const onPrintInvoice = useCallback(
    (value) => {
      dispatch(updatePrintInvoice(value));
    },
    [dispatch],
  );

  const onUpdatePaymentMethod = useCallback(
    (value) => {
      dispatch(updatePaymentMethod(value));
    },
    [dispatch],
  );

  const onSelectVoucher = useCallback(
    (voucher, amount) => {
      dispatch(selectVoucher({ voucher, amount }));
    },
    [dispatch],
  );

  const onClearVoucher = useCallback(
    () => {
      dispatch(clearVoucher());
    },
    [dispatch],
  );

  const onUpdateVoucherAmount = useCallback(
    (amount) => {
      dispatch(updateVoucherAmount(amount));
    },
    [dispatch],
  );

  const onApplyDiscount = useCallback(
    (amount, label) => {
      dispatch(applyDiscount({ amount, label }));
    },
    [dispatch],
  );

  const onClearDiscount = useCallback(
    () => {
      dispatch(clearDiscount());
    },
    [dispatch],
  );

  const onApplyItemDiscount = useCallback(
    (itemId, amount, label) => {
      dispatch(
        applyItemDiscount({ itemId, amount, label, taxConfig: config?.tax, taxIncludedInItemPrice })
      );
    },
    [dispatch],
  );

  const onClearItemDiscount = useCallback(
    (itemId) => {
      dispatch(clearItemDiscount({ itemId, taxConfig: config?.tax, taxIncludedInItemPrice }));
    },
    [dispatch],
  );

  return {
    items,
    invoiceSelected,
    customerSelected,
    activeStep,
    subTotal,
    total,
    tax,
    discount,
    totalItems,
    payment,
    printInvoice,
    paymentMethodSelected,
    voucher,
    voucherAmount,
    //
    onResetItems,
    onSelectInvoice,
    onSelectCustomer,
    onUpdateItems,
    onIncreaseQuantity,
    onDecreaseQuantity,
    onUpdatePrice,
    onNextStep,
    onBackStep,
    onFirstStep,
    onUpdatePayment,
    onPrintInvoice,
    onUpdatePaymentMethod,
    onSelectVoucher,
    onClearVoucher,
    onUpdateVoucherAmount,
    onApplyDiscount,
    onClearDiscount,
    onApplyItemDiscount,
    onClearItemDiscount,
    discountLabel,
  };
}
